import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
	Button,
	Col,
	Form,
	FormFeedback,
	Input,
	Label,
	ModalBody,
	Row,
	Spinner,
	TabContent,
	TabPane,
	Card,
	CardBody,
	Alert,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { Select } from '@mantine/core';

import OSelect from '../../../../../Components/OElements/OSelect';
import {
	getResidueCategoryList,
	getResidueList,
} from '../../../../../helpers/residue/backend_helper';
import { getRespelWarehouseList } from '../../../../../helpers/warehouse/backend_helper';
import {
	addNewResidueIntake,
	updateResidueIntake,
} from '../../../../../slices/residueIntake/thunk';
import {
	setRequireCloseModal,
	cleanResidueIntakeDetail,
} from '../../../../../slices/residueIntake/reducer';
import { residue_config } from '../../../../../config';
import { useFormDictionary } from '../../../../Warehouse/Guide/Forms/FormDictionaryContext';
import {
	parsePeriodStringToRange,
	serializePeriodRange,
} from '../../../../Warehouse/Guide/utils/periodRangeString';
import { getMortalityFieldVisibility } from '../../../../Warehouse/Guide/utils/mortalityFieldVisibility';

const RESPEL_CATEGORY_LIST_PARAMS = {
	residue_type_id: residue_config.REACT_APP_RESPEL_INTAKE_RESIDUE_TYPE_ID || '',
};

const selectResidueIntakeModalState = createSelector(
	state => state.ResidueIntakes,
	r => ({
		isAdding: r.isAdding,
		isUpdating: r.isUpdating,
		requireCloseModal: r.requireCloseModal,
		residueIntakeDetail: r.residueIntakeDetail,
	})
);

const selectEstablishmentTypeId = createSelector(
	state => state.Profile,
	p => ({
		establishmentTypeId:
			p.establishmentActive?.item?.establishment_type_id || '',
	})
);

/** Id de categoría en una fila de residuo (lista plana o populate) */
const getResidueRowCategoryId = r => {
	if (!r) return null;
	const raw =
		r.residue_category_id ??
		r.residue_category?._id ??
		(typeof r.residue_category === 'string' ? r.residue_category : null);
	return raw != null && raw !== '' ? String(raw) : null;
};

/** Una sola petición de residuos (ámbito tipo peligrosos / intake); filtro por categoría solo en UI */
const buildResidueIntakeFetchParams = () => {
	const tid = residue_config.REACT_APP_RESPEL_INTAKE_RESIDUE_TYPE_ID;
	const params = {
		limit: 10000,
		populate: ['residue_category'],
	};
	if (tid) params.residue_type_id = tid;
	return params;
};

const buildInitialValues = data => ({
	warehouse_id: data?.warehouse_id || data?.warehouse?._id || '',
	residue_category_id:
		data?.residue_category_id ||
		data?.residue?.residue_category_id ||
		data?.residue?.residue_category?._id ||
		'',
	residue_id: data?.residue_id || data?.residue?._id || '',
	kilograms: data?.kilograms ?? '',
	intake_date: data?.intake_date ? new Date(data.intake_date) : new Date(),
	establishment_origin_id:
		data?.establishment_origin_id || data?.establishment_origin?._id || '',
	origin_description: data?.origin_description || '',
	document_id: data?.document_id || '',
	especie: data?.especie || '',
	fish_count: data?.fish_count ?? '',
	number_of_bins: data?.number_of_bins ?? '',
	cubic_meters: data?.cubic_meters ?? '',
	ph_ensilage: data?.ph_ensilage ?? '',
	has_flow_meter: !!data?.has_flow_meter,
	period: data?.period || '',
	cultivation_center_origin: data?.cultivation_center_origin || '',
	siep_code_cultivation_center: data?.siep_code_cultivation_center || '',
	cage_origin: data?.cage_origin || '',
	market_restrictions: data?.market_restrictions || '',
	bin_seal_folios: data?.bin_seal_folios || '',
});

const isMongoObjectIdString = value =>
	typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);

const IntakeForm = ({
	dataResidueIntake: dataResidueIntakeProp,
	isEdit,
	handleToggleModal,
	isOpen,
	activeTab,
	establishmentId,
}) => {
	const dispatch = useDispatch();
	const { species, cultivationCenters } = useFormDictionary();

	const speciesOptions = useMemo(
		() =>
			species.map(s => ({
				value: s.name,
				label: s.name,
				item: s,
			})),
		[species]
	);

	const cultivationCenterOptions = useMemo(
		() =>
			cultivationCenters.map(cc => ({
				value: cc.name,
				label: cc.name,
				siep: cc.code_siep,
				item: cc,
			})),
		[cultivationCenters]
	);

	const { isAdding, isUpdating, requireCloseModal, residueIntakeDetail } =
		useSelector(selectResidueIntakeModalState);

	const { establishmentTypeId } = useSelector(selectEstablishmentTypeId);

	const dataResidueIntake = useMemo(() => {
		if (
			isEdit &&
			residueIntakeDetail &&
			residueIntakeDetail._id?.toString() ===
				dataResidueIntakeProp?._id?.toString()
		) {
			return { ...dataResidueIntakeProp, ...residueIntakeDetail };
		}
		return dataResidueIntakeProp;
	}, [isEdit, residueIntakeDetail, dataResidueIntakeProp]);

	const hasConsumption = !!dataResidueIntake?.has_consumption;
	const lockKilogramsFields = isEdit && hasConsumption;

	/* Sin memo, cada render crea un objeto nuevo y enableReinitialize resetea el formulario
	   en bucle (borra categoría/residuo y dispara update depth en OSelect). */
	const intakeFormInitialValues = useMemo(
		() => buildInitialValues(dataResidueIntake),
		[dataResidueIntake]
	);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: intakeFormInitialValues,
		validationSchema: Yup.object({
			warehouse_id: Yup.string().required('Campo requerido'),
			residue_category_id: Yup.string().nullable(),
			residue_id: Yup.string().required('Campo requerido'),
			kilograms: Yup.number()
				.typeError('Debe ser un número')
				.required('Campo requerido')
				.moreThan(0, 'Debe ser mayor a 0'),
			intake_date: Yup.date()
				.typeError('Fecha inválida')
				.required('Campo requerido'),
			ph_ensilage: Yup.number()
				.typeError('Debe ser un número')
				.min(0, 'Mínimo 0')
				.max(14, 'Máximo 14')
				.nullable(),
		}),
		onSubmit: values => {
			const toNumberOrNull = v =>
				v === '' || v === null || v === undefined ? null : Number(v);
			const intakeDate =
				values.intake_date instanceof Date
					? values.intake_date.toISOString()
					: values.intake_date;

			const payload = {
				warehouse_id: values.warehouse_id,
				residue_id: values.residue_id,
				kilograms: Number(values.kilograms),
				intake_date: intakeDate,
				establishment_origin_id: values.establishment_origin_id || null,
				origin_description: values.origin_description || null,
				document_id: values.document_id || null,
				especie: values.especie || null,
				fish_count: toNumberOrNull(values.fish_count),
				number_of_bins: toNumberOrNull(values.number_of_bins),
				cubic_meters: toNumberOrNull(values.cubic_meters),
				ph_ensilage: toNumberOrNull(values.ph_ensilage),
				hydrogen_sulfide: null,
				has_flow_meter:
					values.has_flow_meter == null ? null : !!values.has_flow_meter,
				period: values.period || null,
				cultivation_center_origin: values.cultivation_center_origin || null,
				siep_code_cultivation_center:
					values.siep_code_cultivation_center || null,
				cage_origin: values.cage_origin || null,
				market_restrictions: values.market_restrictions || null,
				bin_seal_folios: values.bin_seal_folios || null,
			};

			if (isEdit) {
				dispatch(
					updateResidueIntake({
						_id: dataResidueIntake._id,
						...payload,
					})
				);
			} else {
				dispatch(addNewResidueIntake(payload));
			}
		},
	});

	useEffect(() => {
		if (isOpen && !isEdit) {
			dispatch(cleanResidueIntakeDetail());
		}
	}, [isOpen, isEdit, dispatch]);

	const preselWarehouseRef = useRef(false);

	useEffect(() => {
		if (!isOpen) {
			preselWarehouseRef.current = false;
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || isEdit) return undefined;
		if (!establishmentId || !isMongoObjectIdString(establishmentId)) return undefined;
		if (preselWarehouseRef.current) return undefined;
		let cancelled = false;
		(async () => {
			try {
				const list = await getRespelWarehouseList(establishmentId);
				if (cancelled || !Array.isArray(list) || list.length !== 1) return;
				const wid = list[0]._id != null ? String(list[0]._id) : '';
				if (!wid) return;
				if (validation.values.warehouse_id) return;
				preselWarehouseRef.current = true;
				validation.setFieldValue('warehouse_id', wid, false);
			} catch {
				/* ignore */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOpen, isEdit, establishmentId]);

	useEffect(() => {
		if (!isOpen || !establishmentId || !isMongoObjectIdString(establishmentId))
			return undefined;
		let cancelled = false;
		const widSnapshot = validation.values.warehouse_id;
		(async () => {
			try {
				const list = await getRespelWarehouseList(establishmentId);
				if (cancelled) return;
				if (!widSnapshot) return;
				const ok = list.some(w => String(w._id) === String(widSnapshot));
				if (!ok) {
					validation.setFieldValue('warehouse_id', '', false);
					toast.info(
						'La bodega no pertenece al establecimiento actual. Selecciona otra bodega.',
						{ autoClose: 4000 },
					);
				}
			} catch {
				/* ignore */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [establishmentId, isOpen]);

	const handleCultivationCenterSelect = center => {
		validation.setFieldValue('cultivation_center_origin', center?.name || '');
		validation.setFieldValue(
			'siep_code_cultivation_center',
			center?.code_siep || ''
		);
	};

	const [allResidues, setAllResidues] = useState([]);
	const [residuesLoading, setResiduesLoading] = useState(false);
	const [residueCategoryOptions, setResidueCategoryOptions] = useState([]);

	useEffect(() => {
		if (!isOpen) return undefined;
		let cancelled = false;
		(async () => {
			try {
				const raw = await getResidueCategoryList({
					...RESPEL_CATEGORY_LIST_PARAMS,
				});
				const list = Array.isArray(raw) ? raw : raw?.data ?? [];
				const opts = list.map(c => ({
					value: String(c._id),
					label: c.name || '-',
				}));
				if (!cancelled) setResidueCategoryOptions(opts);
			} catch {
				if (!cancelled) setResidueCategoryOptions([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return undefined;
		let cancelled = false;
		setResiduesLoading(true);
		(async () => {
			try {
				const raw = await getResidueList(buildResidueIntakeFetchParams());
				const list = Array.isArray(raw) ? raw : raw?.data ?? [];
				if (!cancelled) setAllResidues(list);
			} catch {
				if (!cancelled) setAllResidues([]);
			} finally {
				if (!cancelled) setResiduesLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isOpen]);

	/** Si el filtro de categoría no coincide con el residuo elegido, limpiar residuo */
	useEffect(() => {
		if (lockKilogramsFields || !allResidues.length) return;
		const cat = validation.values.residue_category_id || '';
		const rid = validation.values.residue_id || '';
		if (!cat || !rid) return;
		const res = allResidues.find(r => String(r._id) === String(rid));
		const rcid = getResidueRowCategoryId(res);
		if (res && rcid != null && rcid !== String(cat)) {
			validation.setFieldValue('residue_id', '');
		}
	}, [
		allResidues,
		lockKilogramsFields,
		validation.values.residue_category_id,
		validation.values.residue_id,
	]);

	const residueCategoryFilterId = validation.values.residue_category_id || '';

	const residueSelectItems = useMemo(() => {
		const filtered = !residueCategoryFilterId
			? allResidues
			: allResidues.filter(r => {
					const rcid = getResidueRowCategoryId(r);
					return rcid !== null && rcid === String(residueCategoryFilterId);
			  });
		return filtered.map(r => ({
			item: r,
			value: r._id != null ? String(r._id) : r._id,
			code: r.code,
			label:
				r?.code != null && r?.code !== ''
					? `${r.name || '-'} (${r.code})`
					: r?.name || '-',
		}));
	}, [allResidues, residueCategoryFilterId]);

	useEffect(() => {
		if (isOpen && requireCloseModal) {
			dispatch(setRequireCloseModal(false));
			handleToggleModal();
		}
	}, [requireCloseModal, isOpen, dispatch, handleToggleModal]);

	const {
		showEnsilageFields,
		showViscerasFields,
		showFishCount,
		showNumberOfBins,
		showBinSealFolios,
		showCultivationCenterOrigin,
		showCageOrigin,
		isPecesEnterosPlanta,
		isPecesEnteroCentro,
		isPecesEnterosIneligible,
	} = getMortalityFieldVisibility(
		validation.values.residue_id,
		establishmentTypeId
	);
	const showMortalitySection =
		showEnsilageFields || showCageOrigin || showViscerasFields;

	const periodRangePickerValue = useMemo(
		() => parsePeriodStringToRange(validation.values.period || ''),
		[validation.values.period]
	);

	const submitDisabled =
		isAdding || isUpdating || isPecesEnterosIneligible;

	return (
		<Form
			className='tablelist-form'
			onSubmit={e => {
				e.preventDefault();
				if (Object.keys(validation.errors).length > 0) {
					toast.error(
						'Por favor revisa la información ingresada. Hay campos que requieren corrección.',
						{ autoClose: 4000 }
					);
				}
				validation.handleSubmit();
				return false;
			}}
		>
			<ModalBody className='modal-body'>
				<TabContent activeTab={activeTab}>
					<TabPane tabId='1'>
						{lockKilogramsFields && (
							<Alert color='warning' className='mb-3'>
								<i className='ri-lock-2-line me-2'></i>
								Este ingreso ya tiene movimientos de salida posteriores. Los
								campos de bodega, categoría, residuo y kilogramos están
								bloqueados (D-011). Solo es posible editar metadatos
								descriptivos.
							</Alert>
						)}
						<Row className='g-3'>
							<Col lg={4}>
								<OSelect
									key={`warehouse_${establishmentId || 'na'}_${validation.values.warehouse_id || 'new'}`}
									label='Bodega RESPEL'
									isRequired
									id='warehouse_id'
									name='warehouse_id'
									value={validation.values.warehouse_id || ''}
									requestApi={() =>
										getRespelWarehouseList(establishmentId)
									}
									handleLabelOptions={item =>
										item?.code
											? `${item.name} (${item.code})`
											: item?.name || '-'
									}
									disabled={lockKilogramsFields}
									validation={validation}
								/>
							</Col>
							<Col lg={4}>
								<Select
									label='Categoría de residuo (filtro opcional)'
									placeholder='Todas las categorías'
									data={residueCategoryOptions}
									value={
										validation.values.residue_category_id
											? String(validation.values.residue_category_id)
											: null
									}
									onChange={val =>
										validation.setFieldValue(
											'residue_category_id',
											val ?? ''
										)
									}
									onBlur={() =>
										validation.setFieldTouched(
											'residue_category_id',
											true
										)
									}
									disabled={lockKilogramsFields}
									clearable
									searchable
									nothingFound='Sin resultados'
								/>
							</Col>
							<Col lg={4}>
								{residuesLoading ? (
									<>
										<Label className='form-label'>
											Residuo <span style={{ color: 'red' }}>*</span>
										</Label>
										<div className='text-muted d-flex align-items-center gap-2 py-2'>
											<Spinner size='sm' /> Cargando residuos…
										</div>
									</>
								) : (
									<OSelect
										key={`intake_residue_${dataResidueIntake?._id || 'new'}`}
										label='Residuo'
										isRequired
										id='residue_id'
										name='residue_id'
										value={
											validation.values.residue_id != null &&
											validation.values.residue_id !== ''
												? String(validation.values.residue_id)
												: ''
										}
										requestApi={null}
										itemsSelect={residueSelectItems}
										disabled={lockKilogramsFields}
										validation={validation}
									/>
								)}
							</Col>
							<Col lg={4}>
								<Label for='kilograms' className='form-label'>
									Kilogramos <span style={{ color: 'red' }}>*</span>
								</Label>
								<Input
									name='kilograms'
									id='kilograms'
									type='number'
									step='0.01'
									min='0'
									className='form-control'
									placeholder='Ingrese kilogramos'
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									value={validation.values.kilograms || ''}
									disabled={lockKilogramsFields}
									invalid={
										validation.touched.kilograms &&
										validation.errors.kilograms
											? true
											: false
									}
								/>
								{validation.touched.kilograms &&
								validation.errors.kilograms ? (
									<FormFeedback type='invalid'>
										{validation.errors.kilograms}
									</FormFeedback>
								) : null}
							</Col>
							<Col lg={4}>
								<DatesProvider
									settings={{
										locale: 'es-mx',
										firstDayOfWeek: 1,
										weekendDays: [0],
										timezone: 'UTC',
									}}
								>
									<DatePickerInput
										label='Fecha de ingreso *'
										id='intake_date'
										name='intake_date'
										placeholder='Seleccione fecha'
										value={validation.values.intake_date}
										onChange={date =>
											validation.setFieldValue('intake_date', date)
										}
										onBlur={() =>
											validation.setFieldTouched('intake_date', true)
										}
										valueFormat='DD/MM/YYYY'
										maxDate={new Date()}
										clearable={false}
										disabled={lockKilogramsFields}
										error={
											validation.touched.intake_date &&
											validation.errors.intake_date
												? validation.errors.intake_date
												: ''
										}
									/>
								</DatesProvider>
							</Col>
							<Col lg={4}>
								<Label for='document_id' className='form-label'>
									Documento (opcional)
								</Label>
								<Input
									name='document_id'
									id='document_id'
									type='text'
									className='form-control'
									placeholder='ID o referencia de documento'
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									value={validation.values.document_id || ''}
								/>
							</Col>
							<Col lg={12}>
								<Label for='origin_description' className='form-label'>
									Descripción de origen (opcional)
								</Label>
								<Input
									name='origin_description'
									id='origin_description'
									type='textarea'
									rows={2}
									className='form-control'
									placeholder='Origen del residuo, contexto, etc.'
									onChange={validation.handleChange}
									onBlur={validation.handleBlur}
									value={validation.values.origin_description || ''}
								/>
							</Col>

							{isPecesEnterosIneligible && (
								<Col lg={12}>
									<Alert color='danger' className='mb-0'>
										<i className='ri-error-warning-line me-2'></i>
										<strong>Mortalidad Entera</strong> solo está disponible
										cuando el establecimiento de origen es una{' '}
										<strong>Planta Primaria</strong> o un{' '}
										<strong>Centro de Cultivo</strong>. Cambie el establecimiento
										activo o seleccione otro residuo.
									</Alert>
								</Col>
							)}

							{showMortalitySection && !isPecesEnterosIneligible && (
								<Col lg={12}>
									<Card className='border border-primary mb-0'>
										<CardBody className='p-3'>
											<div className='d-flex align-items-center mb-3'>
												<i className='ri-heart-pulse-line text-primary me-2 fs-5'></i>
												<h6 className='mb-0 text-primary'>
													Datos específicos de Mortalidad
												</h6>
											</div>
											{isPecesEnterosPlanta && (
												<Alert color='warning' className='mb-3 py-2'>
													<i className='ri-building-line me-2'></i>
													<strong>
														Despacho desde Planta Primaria a Reductora
													</strong>
												</Alert>
											)}
											{isPecesEnteroCentro && (
												<Alert color='info' className='mb-3 py-2'>
													<i className='ri-flood-line me-2'></i>
													<strong>
														Contingencia desde Centro de Cultivo a Reductora
													</strong>
												</Alert>
											)}
											<Row className='g-3'>
												<Col lg={6}>
													<OSelect
														label='Especie'
														id='especie'
														name='especie'
														value={validation.values.especie || ''}
														onSelect={item =>
															validation.setFieldValue(
																'especie',
																item?.name || ''
															)
														}
														itemsSelect={speciesOptions}
														validation={validation}
														requestApi={null}
														placeholder='Seleccione especie...'
													/>
												</Col>
												{showFishCount && (
													<Col lg={6}>
														<Label for='fish_count' className='form-label'>
															Nro. de peces
														</Label>
														<Input
															name='fish_count'
															id='fish_count'
															className='form-control'
															placeholder='Ingrese número de peces'
															type='number'
															min='0'
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.fish_count || ''}
														/>
													</Col>
												)}
												{showNumberOfBins && (
													<Col lg={6}>
														<Label for='number_of_bins' className='form-label'>
															N° de Bins
														</Label>
														<Input
															name='number_of_bins'
															id='number_of_bins'
															className='form-control'
															placeholder='Ingrese número de bins'
															type='number'
															min='0'
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.number_of_bins || ''}
														/>
													</Col>
												)}
												{showEnsilageFields && (
													<>
														<Col lg={6}>
															<Label for='cubic_meters' className='form-label'>
																Metros Cúbicos (m³)
															</Label>
															<Input
																name='cubic_meters'
																id='cubic_meters'
																className='form-control'
																placeholder='Ingrese metros cúbicos'
																type='number'
																step='0.01'
																min='0'
																onChange={validation.handleChange}
																onBlur={validation.handleBlur}
																value={validation.values.cubic_meters || ''}
															/>
														</Col>
														<Col lg={6}>
															<Label for='ph_ensilage' className='form-label'>
																pH en caso de ensilaje
															</Label>
															<Input
																name='ph_ensilage'
																id='ph_ensilage'
																className='form-control'
																placeholder='Ingrese el pH'
																type='number'
																step='0.1'
																min='0'
																max='14'
																onChange={validation.handleChange}
																onBlur={validation.handleBlur}
																value={validation.values.ph_ensilage || ''}
																invalid={
																	validation.touched.ph_ensilage &&
																	validation.errors.ph_ensilage
																		? true
																		: false
																}
															/>
															{validation.touched.ph_ensilage &&
															validation.errors.ph_ensilage ? (
																<FormFeedback type='invalid'>
																	{validation.errors.ph_ensilage}
																</FormFeedback>
															) : null}
														</Col>
														<Col lg={6}>
															<div className='form-check form-switch mt-4 pt-2'>
																<Input
																	name='has_flow_meter'
																	id='has_flow_meter'
																	type='switch'
																	checked={
																		validation.values.has_flow_meter || false
																	}
																	onChange={validation.handleChange}
																/>
																<Label
																	for='has_flow_meter'
																	className='form-check-label ms-2'
																>
																	¿Dispone de flujómetro?
																</Label>
															</div>
														</Col>
														<Col lg={12}>
															<DatesProvider
																settings={{
																	locale: 'es-mx',
																	firstDayOfWeek: 1,
																	weekendDays: [0],
																	timezone: 'UTC',
																}}
															>
																<DatePickerInput
																	type='range'
																	label='Periodo que abarca'
																	id='period'
																	name='period'
																	placeholder='Seleccione fecha inicio y fin'
																	value={periodRangePickerValue}
																	onChange={range => {
																		const tuple = Array.isArray(range)
																			? range
																			: [null, null];
																		validation.setFieldValue(
																			'period',
																			serializePeriodRange(tuple),
																			false
																		);
																	}}
																	onBlur={() =>
																		validation.setFieldTouched('period', true)
																	}
																	valueFormat='DD/MM/YYYY'
																	maxDate={new Date()}
																	clearable
																/>
															</DatesProvider>
														</Col>
													</>
												)}
												{showCultivationCenterOrigin && (
													<>
														<Col lg={6}>
															<OSelect
																label='Centro cultivo origen'
																id='cultivation_center_origin'
																name='cultivation_center_origin'
																value={
																	validation.values
																		.cultivation_center_origin || ''
																}
																onSelect={handleCultivationCenterSelect}
																itemsSelect={cultivationCenterOptions}
																validation={validation}
																requestApi={null}
																placeholder='Seleccione centro...'
															/>
														</Col>
														<Col lg={6}>
															<Label
																for='siep_code_cultivation_center'
																className='form-label'
															>
																Código SIEP centro cultivo
															</Label>
															<Input
																name='siep_code_cultivation_center'
																id='siep_code_cultivation_center'
																className='form-control bg-light'
																placeholder='Se completa automáticamente'
																type='text'
																disabled
																readOnly
																value={
																	validation.values
																		.siep_code_cultivation_center || ''
																}
															/>
														</Col>
													</>
												)}
												{showCageOrigin && (
													<Col lg={6}>
														<Label for='cage_origin' className='form-label'>
															Jaula de origen
														</Label>
														<Input
															name='cage_origin'
															id='cage_origin'
															className='form-control'
															placeholder='Ej: 103-106-108-102'
															type='text'
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.cage_origin || ''}
														/>
													</Col>
												)}
												{showBinSealFolios && (
													<Col lg={6}>
														<Label for='bin_seal_folios' className='form-label'>
															Folios de sellos de bins
														</Label>
														<Input
															name='bin_seal_folios'
															id='bin_seal_folios'
															className='form-control'
															placeholder='Ej: 1234-5678-9012'
															type='text'
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={validation.values.bin_seal_folios || ''}
														/>
													</Col>
												)}
												{showViscerasFields && (
													<Col lg={12}>
														<Label
															for='market_restrictions'
															className='form-label'
														>
															Restricciones de mercado
														</Label>
														<Input
															name='market_restrictions'
															id='market_restrictions'
															className='form-control'
															placeholder='Ej: Solo para exportación, no para consumo local'
															type='text'
															onChange={validation.handleChange}
															onBlur={validation.handleBlur}
															value={
																validation.values.market_restrictions || ''
															}
														/>
													</Col>
												)}
											</Row>
										</CardBody>
									</Card>
								</Col>
							)}
						</Row>
					</TabPane>
				</TabContent>
			</ModalBody>
			<div className='modal-footer'>
				<div className='hstack gap-2 modal-footer-add-btn'>
					<Button
						type='button'
						onClick={handleToggleModal}
						className='btn-danger btn-label'
					>
						<i className='icon-x label-icon'></i>
						Cancelar
					</Button>
					<button
						type='submit'
						className='btn btn-label btn-primary'
						id='add-btn'
						disabled={submitDisabled}
					>
						{isAdding || isUpdating ? (
							<i className='label-icon'>
								<Spinner size='sm' />
							</i>
						) : (
							<i className='ri-save-3-line label-icon'></i>
						)}
						{isEdit ? 'Editar' : 'Registrar'} Ingreso
					</button>
				</div>
			</div>
		</Form>
	);
};

export default IntakeForm;
